import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import {
  Content,
  Title,
  Button,
  ProductItem,
  Text,
  ModalLayout,
  HtmlContent,
} from 'components';
import { encrypt } from 'utils/Crypto';
import MessageAlert, { STORE_URL, stringToMoneyFormat } from 'utils';
import { Checkbox, Flex, Select } from 'antd';
import { Button as AntButton } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import Cart from 'assets/icon/cart_icon.svg';
// import Heart from 'assets/icon/heart_icon.svg';

const Recipe = ({ screenSize, products = [], onSubmit }) => {
  /* ===== 기본 세팅 ===== */
  const navigate = useNavigate();
  const [swiperObj, setSwiperObj] = useState(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const [isOpened, setIsOpened] = useState(false);

  /* ===== 탭 관련 상태 ===== */
  const [selectedTab, setSelectedTab] = useState(null);

  React.useEffect(() => {
    if (!products.length) return;

    setSelectedTab(products?.[0]?.group_product_id);
  }, [products]);

  /* ===== 선택 상품 관련 상태 ===== */
  const [selectedProduct, setSelectedProduct] = useState(null);

  React.useEffect(() => {
    if (!selectedTab) return;

    const findItem = products?.find(
      (product) => product?.group_product_id === selectedTab
    );
    setSelectedProduct(findItem);
  }, [selectedTab, products]);

  /* ===== 선택 상품 목록 관련 상태 ===== */
  const [checkedItems, setCheckedItems] = useState([]);

  const handleCheck = (data, checked) => {
    const fid = checkedItems.findIndex(
      (item) => item?.product_id === data?.product_id
    );

    if (fid === -1) {
      setCheckedItems((prev) => [...prev, data]);
    } else {
      setCheckedItems((prev) => [
        ...prev.slice(0, fid),
        ...prev.slice(fid + 1),
      ]);
    }
  };

  /* ===== 총합금액 관련 상태 ===== */
  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    if (!checkedItems.length) return setTotalPrice(0);

    const totalPrice = checkedItems.reduce((sum, item) => {
      const discountPrice =
        item.discount_type === 'R'
          ? item.price * (1 - item.discount / 100)
          : item.price - item.discount;
      return sum + discountPrice;
    }, 0);

    setTotalPrice(totalPrice);
  }, [checkedItems]);

  // const selectedCheckedItems = checkedItems[selectedTab];

  /* ===== 슬라이드 관련 설정 ===== */
  useEffect(() => {
    if (swiperObj) {
      swiperObj.slideTo(0);
      swiperObj.update();
      setIsBeginning(true);
      setIsEnd(false);
      setCheckedItems([]);
    }
  }, [selectedTab, swiperObj]);

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const slidesPerView = useMemo(() => {
    if (screenSize >= 768) return 3;
    return 2;
  }, [screenSize]);

  const [showNavigation, setShowNavigation] = useState(false);

  useEffect(() => {
    if (!selectedProduct) return setShowNavigation(false);

    setShowNavigation(
      (selectedProduct?.products?.length ?? 0) > slidesPerView ? true : false
    );
  }, [selectedProduct, slidesPerView]);

  /* ===== 탭 스크롤 그라데이션 효과 ===== */
  const tabScrollRef = useRef(null);
  const [leftOpacity, setLeftOpacity] = useState(0);
  const [rightOpacity, setRightOpacity] = useState(1);

  const checkScroll = () => {
    const el = tabScrollRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setLeftOpacity(scrollLeft > 0 ? 1 : 0);
      setRightOpacity(scrollLeft + clientWidth < scrollWidth ? 1 : 0);
    }
  };

  useEffect(() => {
    checkScroll();
    const el = tabScrollRef.current;
    el?.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el?.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  /* ===== 장바구니 관련 설정 ===== */
  const [isVisible, setIsVisible] = React.useState(false);
  const [cartItems, setCartItems] = React.useState([]);

  /**
   * 장바구니 모달 핸들러
   * --
   * @param {array} arr
   * @returns
   */
  const handleOpenCartModal = (arr = []) => {
    if (!arr.length) return MessageAlert.info('상품을 선택해주세요!');

    const newArr = arr.map((item) => {
      const { options, ...rest } = item;

      const newOptions = options
        ? options.map((option, index) => ({
          key: `${index}-${rest.product_id}-${option.option_id}`,
          title: option.option_title,
          type: option.type,
          children: option.option.map((op) => ({
            label: `${op.option_name}${op.option_price
              ? ` (+${stringToMoneyFormat(op.option_price)})`
              : ''
              }`,
            value: `${index}-${rest.product_id}-${option.option_id}-${op.option_key}`,
            selected: false,
          })),
          origin: option,
        }))
        : [];

      const groupedOptions = newOptions.reduce(
        (acc, cur) => {
          acc[cur?.type]?.push(cur);
          return acc;
        },
        { REQUIRED: [], ADDITION: [] }
      );

      // 각 그룹별로 정렬
      groupedOptions.REQUIRED.sort((a, b) => a.order - b.order);
      groupedOptions.ADDITION.sort((a, b) => a.order - b.order);

      return {
        ...rest,
        group_options: groupedOptions,
      };
    });

    // 옵션이 있는 상품이 존재하는지 체크
    const isOptionExist = newArr.some((item) => {
      const { group_options } = item;

      const length = Object.values(group_options).reduce((acc, cur) => {
        acc = acc.concat(cur);

        return acc;
      }, []);

      return length.length > 0;
    });

    // 모든 선택 상품이 옵션이 없을 경우 장바구니 생성
    if (!isOptionExist) {
      const mutateData = newArr.map((item) => ({
        ...item,
        product_option: null,
        cart_type: 'DELIVERY',
        cart_num: 1,
      }));

      handleSubmit(mutateData);
    } else {
      setIsVisible(!isVisible);
      setCartItems(newArr);
    }
  };

  /**
   * 옵션 선택값 변경
   * --
   * @param {number} idx
   * @param {string} key
   * @param {string} value
   * @param {string} type [REQUIRED, ADDITION]
   */
  const handleChangeOption = (
    idx = null,
    key = null,
    value = null,
    type = 'REQUIRED'
  ) => {
    if (!idx || !key || !value) return;

    const targetIdx = cartItems.findIndex((item) => item.key === idx);

    setCartItems((prev) => [
      ...prev.slice(0, targetIdx),
      {
        ...prev[targetIdx],
        group_options: {
          ...prev[targetIdx]['group_options'],
          [type]: prev[targetIdx]['group_options'][type].map((item) =>
            item.key !== key
              ? item
              : {
                ...item,
                children: item.children.map((sub) =>
                  sub.value !== value
                    ? { ...sub, selected: false }
                    : { ...sub, selected: true }
                ),
              }
          ),
        },
      },
      ...prev.slice(targetIdx + 1),
    ]);
  };

  /**
   * 옵션 유효성 검사
   * --
   * @param {array} arr
   */
  const handleValidateCheck = (arr = []) => {
    if (!arr.length) return;

    const newArr = arr.map((item) => ({
      ...item,
      group_options: Object.values(item?.group_options)
        .reduce((acc, cur) => {
          acc = acc.concat(cur);
          return acc;
        }, [])
        .map((sub) => ({
          ...sub,
          children: sub?.children?.filter((sub) => sub?.selected),
        })),
    }));

    // 옵션이 있는 상품 중 옵션을 선택하지 않은 상품이 있는지 체크
    const isExist = newArr.some(
      (item) =>
        item?.group_options?.length &&
        item?.group_options?.some((sub) => !sub?.children?.length)
    );

    // 옵션을 선택하지 않은 상품이 존재할 경우
    if (isExist) {
      MessageAlert.info('옵션을 모두 선택해주세요!');
      return;
    }

    const mutateData = newArr.map((item) => ({
      ...item,
      product_option: item?.group_options?.length
        ? item.group_options.map((option) => {
          const { children, origin } = option;

          const selected = children[0].value.split('-').pop();

          return {
            ...origin,
            option: origin.option.filter(
              (sub) => sub.option_key === Number(selected)
            ),
          };
        })
        : null,
      cart_type: 'DELIVERY',
      cart_num: 1,
    }));

    handleSubmit(mutateData);
  };

  /**
   *
   * @param {array} arr
   * @returns
   */
  const handleSubmit = async (arr) => {
    const res = await onSubmit(arr);

    if (!res) return;
    else {
      setIsVisible(false);
      setCartItems([]);
    }
  };

  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: screenSize >= 768 ? 64 : screenSize >= 500 ? 30 : 26,
      lineHeight: screenSize >= 768 ? '88px' : '150%',
      marginBottom: screenSize >= 500 ? 0 : 20,
      paddingRight: 10,
    },

    layoutStyle: {
      borderRadius: 10,
    },

    button: {
      borderRadius: 0,
      width: '100%',
    },
  };

  return (
    <>
      <Content
        maxWidth={1500}
        className="recipe-section main_swiper"
        style={{
          position: 'relative',
          padding: screenSize >= 768 ? '0 0 100px' : '0 0 80px',
        }}
      >
        <Title
          style={{ ...styles.title }}
          className="dela-gothic"
          subButton={
            <Button
              className="goto"
              onClick={() => navigate(`/recipe`)}
              type="default"
            >
              바로가기 <RightOutlined />
            </Button>
          }
        >
          Recommend
        </Title>

        {/* 탭 영역 */}
        <div className="button_wrap" style={{ position: 'relative' }}>
          {/* 왼쪽 그라데이션 */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: 40,
              height: '100%',
              background: 'linear-gradient(to right, white, transparent)',
              opacity: leftOpacity,
              pointerEvents: 'none',
              transition: 'opacity 0.3s',
              zIndex: 1,
            }}
          />
          {/* 오른쪽 그라데이션 */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 40,
              height: '100%',
              background: 'linear-gradient(to left, white, transparent)',
              opacity: rightOpacity,
              pointerEvents: 'none',
              transition: 'opacity 0.3s',
              zIndex: 1,
            }}
          />

          <div
            ref={tabScrollRef}
            style={{
              display: 'flex',
              overflowX: 'auto',
              gap: 12,
              paddingBottom: 4,
              scrollbarWidth: 'none',
            }}
          >
            {products?.map((group) => (
              <Button
                key={`group-${group?.group_product_title}-${group?.group_product_id}`}
                onClick={() => setSelectedTab(group?.group_product_id)}
                className={
                  selectedTab === group?.group_product_id ? '' : 'unsellected'
                }
              >
                {group?.group_product_title}
              </Button>
            ))}
          </div>
        </div>

        {/* 레시피 보기 배너 */}
        <Content
          maxWidth={'100%'}
          padding={0}
          className="recipe_banner"
          style={{
            marginBottom: screenSize >= 768 ? 50 : 20,
            position: 'relative',
            top: 0,
            left: 0,
            border: '1px solid #EEEEEE',
          }}
        >
          <img
            src={
              selectedProduct?.thumbnail_url
                ? `${STORE_URL}/${selectedProduct?.thumbnail_url}`
                : null
            }
            alt={selectedProduct?.group_product_title}
            width={'100%'}
            style={{
              aspectRatio: screenSize >= 768 ? '1 / 0.3' : '1 / 0.5',
              objectFit: 'cover',
              height: '100%',
              display: 'block',
            }}
          />
          <Flex
            vertical
            justify="center"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(0, 0, 0, 0.35)',
              padding: screenSize >= 768 ? '0px 90px' : '0px 30px',
            }}
            gap={screenSize >= 500 ? 50 : 20}
          >
            <Title
              style={{
                fontSize: screenSize >= 768 ? 50 : screenSize >= 500 ? 26 : 20,
                color: 'var(--white-color)',
                flexWrap: 'wrap',
              }}
            >
              {selectedProduct?.group_product_title}
            </Title>
            <Text
              style={{
                fontSize: screenSize >= 768 ? 20 : screenSize >= 500 ? 14 : 12,
                color: 'var(--white-color)',
                marginTop: screenSize >= 500 ? -50 : -20,
              }}
              className="dela-gothic"
            >
              {selectedProduct?.group_product_description}
            </Text>
            <Button
              className={'goto white'}
              style={{ alignSelf: 'flex-start', width: 'auto' }}
              onClick={() => setIsOpened(!isOpened)}
            >
              자세히 보기 <RightOutlined />
            </Button>
          </Flex>
        </Content>

        {/* 상품 리스트 슬라이드 */}
        <div className="recipe_swiper_wrap">
          {/* 왼쪽 화살표 */}
          {showNavigation && (
            <div style={{ ...styles.controlBtn }}>
              <AntButton
                ref={prevRef}
                className={`recipe_slide_left ${isBeginning ? 'disabled' : ''}`}
                onClick={() => swiperObj?.slidePrev()}
                size="large"
              >
                <LeftOutlined />
              </AntButton>
            </div>
          )}

          <div style={{ padding: screenSize >= 768 ? 40 : '30px 20px' }}>
            <Swiper
              className="recipe_swiper"
              modules={[Navigation]}
              spaceBetween={0}
              slidesPerView={slidesPerView}
              onInit={(swiper) => {
                swiper.params.navigation.prevEl = prevRef.current;
                swiper.params.navigation.nextEl = nextRef.current;
                swiper.navigation.init();
                swiper.navigation.update();
                setSwiperObj(swiper);

                // 초기 상태도 설정
                setIsBeginning(swiper.isBeginning);
                setIsEnd(swiper.isEnd);

                setTimeout(() => {
                  swiper.navigation.update();
                }, 0);
              }}
              onSlideChange={(swiper) => {
                setIsBeginning(swiper.isBeginning);
                setIsEnd(swiper.isEnd);
              }}
            >
              {selectedProduct?.products?.map((item, index) => (
                <SwiperSlide key={`product_${index}`} className="recipe_slide">
                  <div
                    // TODO
                    // onClick={() =>
                    //   navigate(
                    //     `/products/${encodeURIComponent(
                    //       encrypt(item.key || item.name)
                    //     )}`
                    //   )
                    // }
                    style={{ display: 'flex', alignItems: 'start' }}
                  >
                    <Checkbox
                      checked={checkedItems.some(
                        (d) => d?.product_id === item?.product_id
                      )}
                      onChange={(e) => handleCheck(item)}
                      style={{ padding: 10 }}
                      disabled={item.isSoldOut === 'Y'}
                    />
                    <ProductItem
                      src={item.src}
                      title={item.title}
                      price={item.price}
                      discount={item.discount}
                      discountType={item.discount_type}
                      discountPrice={item.finalPrice}
                      hovered={false}
                      brand={item.brand}
                      titleStyle={{ marginTop: 10 }}
                      isSoldOut={item.isSoldOut === 'Y' ? true : false}
                      policies={item.policies}
                      onClick={() =>
                        navigate(
                          `/products/${encodeURIComponent(
                            encrypt(item.key || item.name)
                          )}`
                        )
                      }
                    />
                    <button
                      style={{ marginLeft: 8 }}
                      onClick={() => handleOpenCartModal([item])}
                    >
                      <img src={Cart} alt="장바구니" />
                    </button>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          <div className="recipe_price">
            총 금액 <strong>{stringToMoneyFormat(totalPrice, '원')}</strong>
            {/* <button className="wishlist">
            <img src={Heart} alt="찜하기" />
          </button> */}
            <button
              style={{ marginLeft: 8 }}
              onClick={() => handleOpenCartModal(checkedItems)}
            >
              <img src={Cart} alt="장바구니" />
            </button>
          </div>
          {/* 오른쪽 화살표 */}
          {showNavigation && (
            <div style={{ ...styles.controlBtn }}>
              <AntButton
                ref={nextRef}
                className={`recipe_slide_right ${isEnd ? 'disabled' : ''}`}
                onClick={() => swiperObj?.slideNext()}
                size="large"
              >
                <RightOutlined />
              </AntButton>
            </div>
          )}
        </div>
      </Content>

      {/* SECTION: 레시피 모달 */}
      <ModalLayout open={isOpened} onCancel={setIsOpened} closable centered>
        <Content
          maxWidth={'100%'}
          padding={0}
          style={{ maxHeight: '90vh', overflowY: 'auto' }}
        >
          <HtmlContent content={selectedProduct?.group_product_content} />
        </Content>
      </ModalLayout>
      {/* !SECTION: 레시피 모달 */}

      {/* SECTION: 장바구니 모달 */}
      <ModalLayout
        title={'장바구니 담기'}
        open={isVisible}
        onCancel={setIsVisible}
        closable
        centered
        width={520}
        styles={{
          content: {
            ...styles.layoutStyle,
          },
        }}
        footer={
          <Flex gap={8} style={{ marginTop: 50 }}>
            <Button
              type="secondary"
              style={{ ...styles.button }}
              onClick={() => {
                setIsVisible(false);
                setCartItems([]);
              }}
            >
              취소
            </Button>
            <Button
              type="primary"
              style={{ ...styles.button }}
              onClick={() => handleValidateCheck(cartItems)}
            >
              담기
            </Button>
          </Flex>
        }
      >
        <Content maxWidth={'100%'} padding={0}>
          <Flex vertical gap={20}>
            {cartItems.map((item) => (
              <Flex vertical gap={12}>
                {/* SECTION: 상품이미지/브랜드명/상품명 */}
                <Flex gap={8} align="center">
                  <ProductItem.Image
                    src={item?.src}
                    hovered={false}
                    title={item?.title}
                    style={{ width: 60 }}
                  />

                  <Flex vertical gap={4}>
                    <Text size={12} color={'#666666'}>
                      {item?.brand}
                    </Text>
                    <Text size={14} weight={700}>
                      {item?.title}
                    </Text>
                  </Flex>
                </Flex>
                {/* !SECTION: 상품이미지/브랜드명/상품명 */}

                {/* SECTION: 필수 옵션 */}
                {item.group_options.REQUIRED.length ? (
                  <Flex vertical gap={4}>
                    <Text size={12} color={'#999999'}>
                      필수옵션
                    </Text>

                    <Flex vertical gap={4}>
                      {item.group_options.REQUIRED.map((option, index) => (
                        <Select
                          key={`REQUIRED-option-${index}`}
                          value={
                            option.children?.find((sub) => sub.selected)
                              ?.value ?? null
                          }
                          options={option.children}
                          style={{ width: '100%' }}
                          placeholder={'옵션을 선택해주세요'}
                          onChange={(e) =>
                            handleChangeOption(
                              item.key,
                              option.key,
                              e,
                              'REQUIRED'
                            )
                          }
                        />
                      ))}
                    </Flex>
                  </Flex>
                ) : null}

                {/* !SECTION: 필수 옵션 */}

                {/* SECTION: 선택 옵션 */}
                {item.group_options.ADDITION.length ? (
                  <Flex vertical gap={4}>
                    <Text size={12} color={'#999999'}>
                      선택옵션
                    </Text>

                    <Flex vertical gap={4}>
                      {item.group_options.ADDITION.map((option, index) => (
                        <Select
                          key={`ADDITION-option-${index}`}
                          value={
                            option.children?.find((sub) => sub.selected)
                              ?.value ?? null
                          }
                          options={option.children}
                          style={{ width: '100%' }}
                          placeholder={'옵션을 선택해주세요'}
                          onChange={(e) =>
                            handleChangeOption(
                              item.key,
                              option.key,
                              e,
                              'ADDITION'
                            )
                          }
                        />
                      ))}
                    </Flex>
                  </Flex>
                ) : null}
                {/* !SECTION: 선택 옵션 */}
              </Flex>
            ))}
          </Flex>
        </Content>
      </ModalLayout>
      {/* !SECTION: 장바구니 모달 */}
    </>
  );
};

export default Recipe;
